import React, { useState } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import LineChart from "@components/LineChart/LineChart";
import { ReactComponent as Menucast } from "@icons/menucast.svg";
import { getRandom } from "@helpers";
import { useLineChartData } from "@core/apiHooks/useLineChartData";

const startYear = 2017;
const endYear = 2030;
const TestPage = () => {
    // Toggle between different modes
    const [viewMode, setViewMode] = useState("controlled"); // "controlled", "api"

    // API data hook
    const { data: apiLineChartConfig } = useLineChartData();

    // State-based configuration - dynamic controls (only used when viewMode = "controlled")
    const [currentYear, setCurrentYear] = useState(2025);
    const [currentQuarter, setCurrentQuarter] = useState(1);
    const [minValue, setMinValue] = useState(25);
    const [maxValue, setMaxValue] = useState(80);
    const [variance, setVariance] = useState(3);

    // Auto-calculate future years start based on current year
    const futureYearsStart = currentYear + 1;

    // Series configuration - control variables (NOT part of chart config)
    const seriesConfig = [
        {
            id: "appetizer-sampler",
            title: "APPETIZER SAMPLER",
            color: "#00EBF5",
            growthPattern: "exponential"
        },
        {
            id: "honey-mustard-wing",
            title: "HONEY MUSTARD CHICKEN WING",
            color: "#8B5CF6",
            growthPattern: "logarithmic"
        },
        {
            id: "barbecue-wing",
            title: "BARBECUE CHICKEN WING",
            color: "#F59E0B",
            growthPattern: "linear"
        }
    ];

    // Generate years array
    const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);

    // Helper function to generate values based on growth pattern with variance
    const generateValue = (seriesIndex, yearIndex, totalYears) => {
        const progress = yearIndex / (totalYears - 1);
        const { growthPattern } = seriesConfig[seriesIndex];

        // Calculate series-specific min/max with variance
        const seriesVariance = (getRandom() - 0.5) * 2 * variance; // Random variance between -variance and +variance
        const seriesMin = minValue + seriesVariance;
        const seriesMax = maxValue + seriesVariance;

        let baseValue;
        switch (growthPattern) {
            case "exponential":
                baseValue = seriesMin + (seriesMax - seriesMin) * Math.pow(progress, 0.5);
                break;
            case "logarithmic":
                baseValue = seriesMin + (seriesMax - seriesMin) * Math.log(1 + progress * (Math.E - 1)) / Math.log(Math.E);
                break;
            case "linear":
            default:
                baseValue = seriesMin + (seriesMax - seriesMin) * progress;
                break;
        }

        // Add some random noise to make it more realistic
        const noise = (getRandom() - 0.5) * 2; // +/- 1 unit of noise
        return baseValue + noise;
    };

    // Generate dynamic data
    const lineChartConfig = {
        series: seriesConfig.map((config, seriesIndex) => ({
            id: config.id,
            title: config.title,
            color: config.color,
            isActive: true,
            data: years.map((year, yearIndex) => {
                if (year === currentYear) {
                    // Generate quarterly data for current year
                    const quarterValues = [1, 2, 3, 4].map(() => generateValue(seriesIndex, yearIndex, years.length));

                    return {
                        label: year.toString(),
                        points: quarterValues.map((value, qIndex) => ({
                            value: Number.parseFloat(value.toFixed(1)),
                            label: `Q${qIndex + 1}`,
                            isActual: qIndex < currentQuarter
                        })),
                    };
                } else {
                    // Single point for other years
                    const value = generateValue(seriesIndex, yearIndex, years.length);
                    return {
                        label: year.toString(),
                        points: [{ value: Number.parseFloat(value.toFixed(1)) }],
                        isDashed: year >= futureYearsStart
                    };
                }
            })
        })),
        showTooltip: true,
        dashedLineStartIndex: years.indexOf(futureYearsStart),
        xAxisLabel: "Year",
        yAxisLabel: "Menu Penetration (%)"
    };

    // Choose which config to use
    const activeLineChartConfig = viewMode === "api" ? apiLineChartConfig : lineChartConfig;

    const printValue = (value) => `${value.toFixed(1)}%`;
    const printLabel = (value) => `${value.toFixed(1)}%`;

    return (
        <div style={{ padding: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "20px" }}>
                <h1 style={{ margin: 0 }}>LineChart Test Page - Working!</h1>
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "10px 15px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "6px",
                    border: "1px solid #dee2e6"
                }}>
                    <span style={{ fontWeight: "600", color: "#495057" }}>
                        View Mode:
                    </span>
                    <label style={{ display: "flex", alignItems: "center", gap: "5px", cursor: "pointer" }}>
                        <input
                            type="radio"
                            name="viewMode"
                            checked={viewMode === "controlled"}
                            onChange={() => setViewMode("controlled")}
                        />
                        <span>Controlled Config</span>
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "5px", cursor: "pointer" }}>
                        <input
                            type="radio"
                            name="viewMode"
                            checked={viewMode === "api"}
                            onChange={() => setViewMode("api")}
                        />
                        <span>API Preview</span>
                    </label>
                </div>
            </div>
            {viewMode === "controlled" && (
                <div style={{
                    backgroundColor: "#f8f9fa",
                    padding: "20px",
                    borderRadius: "8px",
                    marginBottom: "20px",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                    gap: "15px"
                }}>
                    <h3 style={{ margin: "0 0 15px 0", color: "#333", gridColumn: "1 / -1" }}>🎛️ Dynamic Chart
                        Controls</h3>

                    <div>
                        <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", color: "#555" }}>
                            Current Year: {currentYear}
                        </label>
                        <input
                            type="range"
                            min={startYear}
                            max={endYear}
                            value={currentYear}
                            onChange={(e) => setCurrentYear(Number.parseInt(e.target.value))}
                            style={{ width: "100%" }}
                        />
                    </div>

                    <div>
                        <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", color: "#555" }}>
                            Current Quarter: Q{currentQuarter}
                        </label>
                        <input
                            type="range"
                            min={1}
                            max={4}
                            value={currentQuarter}
                            onChange={(e) => setCurrentQuarter(Number.parseInt(e.target.value))}
                            style={{ width: "100%" }}
                        />
                    </div>

                    <div>
                        <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", color: "#555" }}>
                            Min Value: {minValue.toFixed(1)}%
                        </label>
                        <input
                            type="range"
                            min={0}
                            max={50}
                            step={0.5}
                            value={minValue}
                            onChange={(e) => setMinValue(Number.parseFloat(e.target.value))}
                            style={{ width: "100%" }}
                        />
                    </div>

                    <div>
                        <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", color: "#555" }}>
                            Max Value: {maxValue.toFixed(1)}%
                        </label>
                        <input
                            type="range"
                            min={minValue + 5}
                            max={100}
                            step={0.5}
                            value={maxValue}
                            onChange={(e) => setMaxValue(Number.parseFloat(e.target.value))}
                            style={{ width: "100%" }}
                        />
                    </div>

                    <div>
                        <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", color: "#555" }}>
                            Variance: ±{variance.toFixed(1)}%
                        </label>
                        <input
                            type="range"
                            min={0}
                            max={10}
                            step={0.5}
                            value={variance}
                            onChange={(e) => setVariance(Number.parseFloat(e.target.value))}
                            style={{ width: "100%" }}
                        />
                    </div>

                    <div style={{
                        gridColumn: "1 / -1",
                        padding: "10px",
                        backgroundColor: "#e9ecef",
                        borderRadius: "5px",
                        fontSize: "14px",
                        color: "#6c757d"
                    }}>
                        <strong>Auto-calculated:</strong> Future years (dashed lines) start from {futureYearsStart}
                    </div>
                </div>
            )}

            <div style={{
                display: "flex",
                gap: "20px",
                marginTop: "20px",
                height: "400px"
            }}>
                {/* Chart Section - 70% */}
                <div style={{
                    flex: "0 0 70%",
                    padding: "20px",
                    borderRadius: "8px",
                    backgroundColor: "#1a1a1a"
                }}>
                    <AutoSizer>
                        {({ height, width }) => (
                            <LineChart
                                config={activeLineChartConfig}
                                width={width}
                                height={height}
                                printValue={printValue}
                                printLabel={printLabel}
                                haiku={Menucast}
                            />
                        )}
                    </AutoSizer>
                </div>

                {/* JSON Config Section - 30% */}
                <div style={{
                    flex: "0 0 30%",
                    padding: "20px",
                    borderRadius: "8px",
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #dee2e6"
                }}>
                    <h4 style={{ margin: "0 0 15px 0", color: "#333", fontSize: "16px" }}>
                        📄 Chart Configuration
                    </h4>
                    <div style={{
                        height: "calc(100% - 40px)",
                        overflowY: "auto",
                        backgroundColor: "#2d3748",
                        borderRadius: "6px",
                        padding: "15px",
                        fontFamily: "Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, monospace",
                        fontSize: "12px",
                        lineHeight: "1.4"
                    }}>
                            <pre style={{
                                margin: 0,
                                color: "#e2e8f0",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word"
                            }}>
                                {JSON.stringify(activeLineChartConfig, null, 2)}
                            </pre>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TestPage;